import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAxios } from '../../core/_axios';
import BaseModal from '../../components/_BaseModal';
import { Check, Pencil, AlertCircle, Cpu, Zap, Settings } from 'lucide-react';
import { usePageHeader } from '../../contexts/PageHeaderContext';
import usePageTitle from '../../hooks/usePageTitle';

interface AiFeature {
  key: string;
  name: string;
  description: string;
  model_type: 'quality' | 'economical' | 'custom';
  custom_model?: string;
  assigned_model: string;
  is_active: boolean;
}

interface AiConfiguration {
  models: {
    quality: string;
    economical: string;
  };
  features: AiFeature[];
}

const AiModelsSettings: React.FC = () => {
  usePageTitle('System Settings - AI Models');
  const { setPageHeader } = usePageHeader();
  const location = useLocation();
  const axios = useAxios();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configuration, setConfiguration] = useState<AiConfiguration | null>(null);
  const [editingModel, setEditingModel] = useState<'quality' | 'economical' | null>(null);
  const [editingFeature, setEditingFeature] = useState<AiFeature | null>(null);
  const [modelValue, setModelValue] = useState('');
  const [featureModelType, setFeatureModelType] = useState<'quality' | 'economical' | 'custom'>('quality');
  const [customModel, setCustomModel] = useState('');

  const availableModels = [
    'gpt-5', 'gpt-5-mini', 'gpt-5-nano',
    'gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo',
    'gpt-3.5-turbo'
  ];

  // Create navigation items
  const navigationItems = useMemo(() => (
    <div className="flex gap-4 text-sm">
      <Link 
        to="/admin/system-settings/ai-models" 
        className="text-primary font-semibold"
      >
        AI Models
      </Link>
      <Link 
        to="/admin/system-settings/ai-prompts/transcription"
        className="text-gray-600 hover:text-primary"
      >
        AI Prompts
      </Link>
    </div>
  ), []);

  // Set up page header with navigation
  useEffect(() => {
    setPageHeader('System Settings', navigationItems);
    return () => setPageHeader(null);
  }, [setPageHeader, navigationItems]);

  useEffect(() => {
    fetchConfiguration();
  }, []);

  const fetchConfiguration = async () => {
    try {
      const response = await axios.get('/api/v1/app/authenticated/admin/ai-configuration');
      setConfiguration(response.data);
    } catch (error) {
      console.error('Failed to fetch AI configuration:', error);
      // If the endpoint fails (likely because migration hasn't run), 
      // fall back to the email-ai endpoint for basic model info
      try {
        const fallbackResponse = await axios.get('/api/v1/app/authenticated/admin/system-settings/email-ai');
        setConfiguration({
          models: {
            quality: fallbackResponse.data.quality_model || 'gpt-5',
            economical: fallbackResponse.data.economical_model || 'gpt-5-mini'
          },
          features: []
        });
      } catch (fallbackError) {
        console.error('Failed to fetch fallback configuration:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditModel = (type: 'quality' | 'economical') => {
    setEditingModel(type);
    setModelValue(configuration?.models[type] || '');
  };

  const handleSaveModel = async () => {
    if (!editingModel) return;

    setSaving(true);
    try {
      // Try the new endpoint first
      const payload: any = {};
      payload[`${editingModel}_model`] = modelValue;

      try {
        await axios.put('/api/v1/app/authenticated/admin/ai-configuration/models', payload);
      } catch (error) {
        // Fall back to updating via system settings if new endpoint fails
        const settingKey = editingModel === 'quality' ? 'ai_quality_model' : 'ai_economical_model';
        await axios.put(`/api/v1/app/authenticated/admin/system-settings/${settingKey}`, {
          value: modelValue
        });
      }

      await fetchConfiguration();
      setEditingModel(null);
    } catch (error) {
      console.error('Failed to update model:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleEditFeature = (feature: AiFeature) => {
    setEditingFeature(feature);
    setFeatureModelType(feature.model_type);
    setCustomModel(feature.custom_model || '');
  };

  const handleSaveFeature = async () => {
    if (!editingFeature) return;

    setSaving(true);
    try {
      await axios.put(`/api/v1/app/authenticated/admin/ai-configuration/features/${editingFeature.key}`, {
        model_type: featureModelType,
        custom_model: featureModelType === 'custom' ? customModel : null
      });
      await fetchConfiguration();
      setEditingFeature(null);
    } catch (error) {
      console.error('Failed to update feature:', error);
    } finally {
      setSaving(false);
    }
  };

  const getModelBadgeColor = (modelType: string) => {
    switch (modelType) {
      case 'quality':
        return 'bg-blue-100 text-blue-800';
      case 'economical':
        return 'bg-green-100 text-green-800';
      case 'custom':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getModelIcon = (modelType: string) => {
    return modelType === 'quality' ? <Cpu className="w-4 h-4" /> : <Zap className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!configuration) {
    return (
      <div className="alert alert-danger">
        <AlertCircle className="w-5 h-5 mr-2" />
        Failed to load AI configuration
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Global Models Section */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title flex items-center">
            <Settings className="w-5 h-5 mr-2" />
            Global AI Models
          </h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Quality Model */}
            <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <Cpu className="w-5 h-5 text-blue-600 mr-2" />
                    <h4 className="font-semibold text-gray-900">Highest Quality Model</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Used for critical operations requiring best accuracy
                  </p>
                  <div className="flex items-center">
                    <code className="bg-blue-50 text-blue-700 px-3 py-1 rounded font-mono text-sm">
                      {configuration.models.quality}
                    </code>
                  </div>
                </div>
                <button
                  onClick={() => handleEditModel('quality')}
                  className="btn btn-sm btn-light ml-3"
                  title="Edit model"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Economical Model */}
            <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <Zap className="w-5 h-5 text-green-600 mr-2" />
                    <h4 className="font-semibold text-gray-900">Economical & Fast Model</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Cost-optimized for bulk operations
                  </p>
                  <div className="flex items-center">
                    <code className="bg-green-50 text-green-700 px-3 py-1 rounded font-mono text-sm">
                      {configuration.models.economical}
                    </code>
                  </div>
                </div>
                <button
                  onClick={() => handleEditModel('economical')}
                  className="btn btn-sm btn-light ml-3"
                  title="Edit model"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Assignments Section */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Feature Model Assignments</h3>
          <div className="card-toolbar">
            <span className="text-sm text-gray-600">
              Configure which model each AI feature uses
            </span>
          </div>
        </div>
        <div className="card-body p-0">
          {configuration.features.length === 0 ? (
            <div className="p-8 text-center">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">Feature configuration not available yet</p>
              <p className="text-sm text-gray-500">
                Please run database migrations to enable feature-specific model assignments.
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-rounded table-striped">
                <thead>
                  <tr className="fw-semibold fs-6 text-gray-800">
                    <th className="min-w-250px">Feature</th>
                    <th className="min-w-125px">Model Type</th>
                    <th className="min-w-150px">Assigned Model</th>
                    <th className="min-w-100px text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {configuration.features.map((feature) => (
                    <tr key={feature.key}>
                      <td>
                        <div>
                          <div className="fw-semibold">{feature.name}</div>
                          <div className="text-gray-600 text-sm">{feature.description}</div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${getModelBadgeColor(feature.model_type)} inline-flex items-center`}>
                          {feature.model_type === 'quality' && <Cpu className="w-3 h-3 mr-1" />}
                          {feature.model_type === 'economical' && <Zap className="w-3 h-3 mr-1" />}
                          {feature.model_type === 'custom' && <Settings className="w-3 h-3 mr-1" />}
                          {feature.model_type}
                        </span>
                      </td>
                      <td>
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                          {feature.assigned_model}
                        </code>
                      </td>
                      <td className="text-end">
                        <button
                          onClick={() => handleEditFeature(feature)}
                          className="btn btn-sm btn-light-primary"
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Configure
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit Model Modal */}
      {editingModel && (
        <BaseModal
          isOpen={true}
          setIsOpen={() => setEditingModel(null)}
          title={`Edit ${editingModel === 'quality' ? 'Highest Quality' : 'Economical & Fast'} Model`}
          actions={
            <>
              <button
                type="button"
                className="btn btn-light me-2"
                onClick={() => setEditingModel(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveModel}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="form-label">Select Model</label>
              <select
                className="form-select"
                value={modelValue}
                onChange={(e) => setModelValue(e.target.value)}
              >
                {availableModels.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
              <div className="form-text">
                Choose the OpenAI model to use for {editingModel === 'quality' ? 'high-quality' : 'economical'} operations
              </div>
            </div>
          </div>
        </BaseModal>
      )}

      {/* Edit Feature Modal */}
      {editingFeature && (
        <BaseModal
          isOpen={true}
          setIsOpen={() => setEditingFeature(null)}
          title={`Configure: ${editingFeature.name}`}
          actions={
            <>
              <button
                type="button"
                className="btn btn-light me-2"
                onClick={() => setEditingFeature(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveFeature}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-4">{editingFeature.description}</p>
              <label className="form-label">Model Assignment</label>
              <div className="space-y-3">
                <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="modelType"
                    value="quality"
                    checked={featureModelType === 'quality'}
                    onChange={(e) => setFeatureModelType('quality')}
                    className="form-check-input me-3"
                  />
                  <div className="flex-1">
                    <div className="flex items-center">
                      <Cpu className="w-4 h-4 text-blue-600 mr-2" />
                      <span className="font-medium">Use Quality Model</span>
                    </div>
                    <span className="text-sm text-gray-600">
                      Currently: <code>{configuration?.models.quality}</code>
                    </span>
                  </div>
                </label>

                <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="modelType"
                    value="economical"
                    checked={featureModelType === 'economical'}
                    onChange={(e) => setFeatureModelType('economical')}
                    className="form-check-input me-3"
                  />
                  <div className="flex-1">
                    <div className="flex items-center">
                      <Zap className="w-4 h-4 text-green-600 mr-2" />
                      <span className="font-medium">Use Economical Model</span>
                    </div>
                    <span className="text-sm text-gray-600">
                      Currently: <code>{configuration?.models.economical}</code>
                    </span>
                  </div>
                </label>

                <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="modelType"
                    value="custom"
                    checked={featureModelType === 'custom'}
                    onChange={(e) => setFeatureModelType('custom')}
                    className="form-check-input me-3 mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <Settings className="w-4 h-4 text-purple-600 mr-2" />
                      <span className="font-medium">Use Custom Model</span>
                    </div>
                    {featureModelType === 'custom' && (
                      <select
                        className="form-select mt-2"
                        value={customModel}
                        onChange={(e) => setCustomModel(e.target.value)}
                      >
                        <option value="">Select a model...</option>
                        {availableModels.map((model) => (
                          <option key={model} value={model}>
                            {model}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </label>
              </div>
            </div>
          </div>
        </BaseModal>
      )}
    </div>
  );
};

export default AiModelsSettings;